/**
 * Compact language switcher — globe icon dropdown.
 * Persists selection to localStorage via the i18n detector.
 */

import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGUAGES } from "../i18n";
import "./LanguageSwitcher.css";

export const LanguageSwitcher = () => {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const current =
    SUPPORTED_LANGUAGES.find((l) => l.code === (i18n.language || "en").split("-")[0]) ||
    SUPPORTED_LANGUAGES[0];

  const choose = (code) => {
    i18n.changeLanguage(code);
    setOpen(false);
  };

  return (
    <div className="lang-switcher" ref={ref} data-testid="language-switcher">
      <button
        className="lang-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-label={t("common.language")}
        title={t("common.language")}
        data-testid="language-switcher-trigger"
      >
        <span className="lang-globe" aria-hidden>🌐</span>
        <span className="lang-current">{current.native}</span>
        <span className="lang-caret" aria-hidden>▾</span>
      </button>

      {open && (
        <div className="lang-menu" role="menu" data-testid="language-switcher-menu">
          {SUPPORTED_LANGUAGES.map((l) => (
            <button
              key={l.code}
              role="menuitem"
              className={`lang-option ${l.code === current.code ? "active" : ""}`}
              onClick={() => choose(l.code)}
              data-testid={`language-option-${l.code}`}
            >
              <span className="lang-native">{l.native}</span>
              <span className="lang-label">{l.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
